import type { MonthGroup } from '@/cmsBackend/transformers/types/featureAnnouncement';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import styles from './ProductUpdatesMonthSelector.module.scss';

export interface ProductUpdatesMonthSelectorProps {
  months: MonthGroup[];
  activeMonthKey?: string;
  /** Month currently being fetched after a chip click; shows a busy spinner on that chip. */
  pendingMonthKey?: string;
  onMonthSelect: (monthKey: string) => void;
  selectorRef?: React.Ref<HTMLDivElement>;
  className?: string;
}

export const ProductUpdatesMonthSelector: React.FC<ProductUpdatesMonthSelectorProps> = ({
  months,
  activeMonthKey,
  pendingMonthKey,
  onMonthSelect,
  selectorRef,
  className,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const element = scrollContainerRef.current;
    if (!element) {
      return;
    }
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [months]);

  const scrollForward = () => {
    scrollContainerRef.current?.scrollBy({ left: 280, behavior: 'smooth' });
  };

  if (months.length === 0) {
    return null;
  }

  return (
    <div ref={selectorRef} className={classNames(styles.wrapper, className)}>
      <div className={styles.selector}>
        <div
          ref={scrollContainerRef}
          className={styles.scrollContainer}
          onScroll={updateScrollState}
          role="tablist"
          aria-label="Product update months"
        >
          <div className={styles.chipTrack}>
            {months.map((month) => {
              const isPending = month.key === pendingMonthKey;
              return (
                <button
                  key={month.key}
                  className={classNames(styles.chip, {
                    [styles.chipActive]: month.key === activeMonthKey,
                    [styles.chipPending]: isPending,
                  })}
                  type="button"
                  role="tab"
                  aria-selected={month.key === activeMonthKey}
                  aria-busy={isPending}
                  disabled={Boolean(pendingMonthKey) && !isPending}
                  onClick={() => onMonthSelect(month.key)}
                >
                  {month.label}
                  {isPending && (
                    <span className={styles.chipSpinner} aria-hidden="true" role="presentation" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {canScrollRight && <div className={styles.gradient} aria-hidden="true" />}
      {canScrollRight && (
        <button
          className={styles.arrowButton}
          type="button"
          aria-label="Show older product update months"
          onClick={scrollForward}
        >
          <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );
};
